
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useEffect } from "react";

const About = () => {
  useEffect(() => {
    // SEO optimization
    document.title = "About Spharaka Networks - AI-Driven Cybersecurity Solutions";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about Spharaka Networks Private Limited, a deep-tech cybersecurity company from India building advanced AI-driven security products for global enterprises. Connect. Protect. Simplify.');
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1" role="main">
        {/* Optimized Hero Section with Background Image */}
        <div className="relative py-24 md:py-32 bg-cover bg-center bg-no-repeat">
          <picture className="absolute inset-0">
            <source 
              media="(max-width: 768px)" 
              srcSet="/lovable-uploads/852f54e1-9cd4-4a8e-af58-6d73bd3cef90.png"
            />
            <div 
              className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('/lovable-uploads/852f54e1-9cd4-4a8e-af58-6d73bd3cef90.png')`
              }}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
          <div className="container mx-auto px-4 md:px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white drop-shadow-lg">About Spharaka Networks</h1>
              <p className="text-xl text-white/90 drop-shadow-md">
                Connect. Protect. Simplify.
              </p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-16 md:py-20 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto space-y-12">
              <div>
                <p className="text-lg text-gray-700 mb-6 text-justify">
                  At Spharaka Networks Private Limited, we are reimagining the future of cybersecurity through the power of Agentic AI, 
                  an intelligent system that learns, reasons, and collaborates alongside human defenders. Our platform 
                  connects fragmented security layers, protects enterprise assets proactively, and simplifies threat 
                  management across the organization.
                </p>
                <p className="text-lg text-gray-700 mb-6 text-justify">
                  Spharaka Networks is a deep-tech cybersecurity company from India, building advanced security products for the world. 
                  Born from the frustration of siloed tools and reactive defense models, Spharaka Networks Private Limited restores clarity 
                  and control. Our AuraXP detects, investigates, explains, and responds to threats with speed and 
                  precision, prioritizing what matters most.
                </p>
                <p className="text-lg text-gray-700 mb-6 text-justify">
                  From CISOs needing unified risk visibility, to threat hunters and SOC analysts seeking context-rich, 
                  role-based insights, Spharaka Networks Private Limited delivers meaningful intelligence at every level. Whether you're a lean 
                  startup, SMB or a Fortune 500 enterprise, our goal is simple: Make cybersecurity smarter, more 
                  transparent, and radically more effective for everyone.
                </p>
              </div>

              <div>
                <h2 className="text-3xl font-bold mb-6">Vision</h2>
                <p className="text-lg text-gray-700 text-justify">
                  A world where Individuals, Organizations & Societies thrives in a digital space that is Secure, Resilient, and Trusted by all.
                </p>
              </div>

              <div>
                <h2 className="text-3xl font-bold mb-6">Mission</h2>
                <p className="text-lg text-gray-700 text-justify">
                  Build with Speed, Precision, and Confidence to Transform how the world experiences Cybersecurity.
                </p>
              </div>

              {/* New Banner Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8 border border-blue-100">
                <div className="text-center">
                  <p className="text-lg text-gray-800 leading-relaxed text-justify max-w-4xl mx-auto">
                    Spharaka Networks Private Limited is a next-generation cybersecurity company revolutionizing the future of intelligent threat 
                    defence through Agentic AI. Designed for enterprises for all sizes, our Products and Platform brings 
                    together proactive threat detection, autonomous investigation, threat hunting, unified risk visibility 
                    and real-time response into a seamless, trusted security ecosystem built to Connect, Protect, and Simplify.
                  </p>
                </div>
              </div>

              {/* Leadership Section */}
              <div>
                <h2 className="text-3xl font-bold mb-8">Leadership</h2>
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Rajeev Raj */}
                  <div className="flex flex-col items-center text-center p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="w-32 h-32 mb-4 rounded-full overflow-hidden">
                      <img 
                        src="/lovable-uploads/d64f4cc8-51dd-4bd2-a615-50f6ba6d8efc.png" 
                        alt="Rajeev Raj, Founder & CEO of Spharaka Networks"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Rajeev Raj</h3>
                    <p className="text-gray-600 mb-3">Founder & CEO</p>
                    <a 
                      href="https://www.linkedin.com/in/rajeevrangaraj/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077b5] text-white rounded-lg hover:bg-[#005885] transition-colors"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      LinkedIn
                    </a>
                  </div>

                  {/* Ravikumar Nalluri */}
                  <div className="flex flex-col items-center text-center p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="w-32 h-32 mb-4 rounded-full overflow-hidden">
                      <img 
                        src="/images/ravikumar-nalluri.jpg" 
                        alt="Ravikumar Nalluri, Co-founder and CTO of Spharaka Networks"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Ravikumar Nalluri</h3>
                    <p className="text-gray-600 mb-3">Co-founder and CTO</p>
                    <a 
                      href="https://www.linkedin.com/in/ravikumar-nalluri/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077b5] text-white rounded-lg hover:bg-[#005885] transition-colors"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>

              {/* Recognitions & Registrations Section */}
              <div>
                <h2 className="text-3xl font-bold mb-8">Recognitions & Registrations</h2>
                
                {/* Startup India Registration Subsection */}
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold mb-8 text-center text-gray-800">Recognitions</h3>
                  <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
                    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 text-center hover:shadow-xl transition-shadow">
                      <div className="mb-4">
                        <img 
                          src="/images/startup-india-logo.jpg" 
                          alt="DPIIT Startup India Logo"
                          className="w-32 h-32 object-contain mx-auto"
                        />
                      </div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Startup India</h4>
                      <p className="text-xs text-gray-500 mb-1">DIPP222457</p>
                      <p className="text-xs text-gray-400">Government of India</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 text-center hover:shadow-xl transition-shadow">
                      <div className="mb-4">
                        <img 
                          src="/images/msme-logo.jpg" 
                          alt="MSME Registration Logo"
                          className="w-32 h-32 object-contain mx-auto"
                        />
                      </div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">MSME Registration</h4>
                      <p className="text-xs text-gray-500 mb-1">UDYAM-TS-20-0149298</p>
                      <p className="text-xs text-gray-400">Government of India</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 text-center hover:shadow-xl transition-shadow">
                      <div className="mb-4">
                        <img 
                          src="/images/gem-logo.png" 
                          alt="Government e-Marketplace (GeM) Logo"
                          className="w-32 h-32 object-contain mx-auto"
                        />
                      </div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">GeM Registration</h4>
                      <p className="text-xs text-gray-500 mb-1">Registered Seller</p>
                      <p className="text-xs text-gray-400">Government of India</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 text-center hover:shadow-xl transition-shadow">
                      <div className="mb-4">
                        <img 
                          src="/images/nasscom-deeptech-club-logo.png" 
                          alt="NASSCOM DeepTech Club Logo"
                          className="w-32 h-32 object-contain mx-auto"
                        />
                      </div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">NASSCOM DeepTech Club</h4>
                      <p className="text-xs text-gray-500 mb-1">Launchpad Program</p>
                      <p className="text-xs text-gray-400">Selected Member</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* University Collaborations Section */}
              <div>
                <h2 className="text-3xl font-bold mb-8">University Collaborations</h2>
                <div className="flex justify-center">
                  <a 
                    href="https://christuniversity.in/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow inline-block"
                  >
                    <img 
                      src="/images/christ-university-logo.png" 
                      alt="Christ University Bengaluru India - Excellence in Service"
                      className="w-auto h-32 object-contain"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default About;
